import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Row, Col } from 'react-flexbox-grid';
import { Paper, Subheader, SelectField, MenuItem } from 'material-ui';
import { get } from 'lodash';
import { connect, useDispatch, useSelector } from 'react-redux';
import convert from 'utils/dateTools';
import pallet from 'constants/colors';
import Loading from '../../components/Loading';
import PanelMessage from '../../components/panels/PanelMessage';
import StatusIcon from '../../components/StatusIcon';
import getAppraisalTitle from '../utils/getAppraisalTitle';
import { fetchAppraisal } from '../../actions/Appraisal';
import { getPMEmployeeAppraisals } from './api';
import { STATUS } from './appraisalEnums';

const { STARTED, FINISHED, NONE } = STATUS;

// Helper function to convert numeric status to string status (for old appraisals)
const convertStatus = (numericStatus) => {
  if (numericStatus === FINISHED) return 'Finished';
  if (numericStatus === STARTED) return 'Started';
  return 'None';
};

// Helper function to normalize old appraisal data to match new structure
const normalizeOldAppraisal = appraisal => ({
  ...appraisal,
  type: 'old',
  // Map old field names to new ones for consistent display
  PMEmployeeAppraisalID: appraisal.PMAppraisalID,
  Title: { IS: appraisal.PMFormTitle },
  DefaultLanguage: 'IS',
  ManagerEmployeeName: appraisal.ManagerName,
  FinalDueDate: appraisal.FinalAppraisalDate,
  // Convert numeric status to string status
  EmployeeStatus: convertStatus(appraisal.EmployeeStatus),
  ManagerStatus: convertStatus(appraisal.ManagerStatus),
  FinalStatus: convertStatus(appraisal.FinalStatus),
});

const styles = {
  header: {
    paddingLeft: 0,
    paddingBottom: 8,
    fontSize: 20,
  },
  appraisal: {
    padding: 16,
    marginBottom: 16,
    cursor: 'pointer',
    transition: 'box-shadow 0.3s',
  },
  title: {
    fontSize: 18,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  date: {
    fontSize: 14,
    color: 'rgba(0, 0, 0, 0.54)',
    marginBottom: 16,
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  label: {
    fontSize: 12,
    color: 'rgba(0, 0, 0, 0.54)',
  },
  value: {
    fontSize: 16,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  statusMessage: {
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'right',
  },
};

const PMAppraisals = ({ valuesByKey }, context) => {
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [year, setYear] = useState(0);
  const [newAppraisals, setNewAppraisals] = useState(null); // null = not yet fetched

  const dispatch = useDispatch();
  const {
    Appraisal: { appraisals: allOldAppraisals, loading: appraisalLoading },
  } = useSelector(state => state);

  // Fetch old appraisals into redux store on mount
  useEffect(() => {
    dispatch(fetchAppraisal());
  }, []);

  // Fetch new appraisals once on mount
  useEffect(() => {
    getPMEmployeeAppraisals()
      .then(({ data = [] }) => {
        const filtered = data
          .filter(a => a.EmployeeDetailID === Number(localStorage.DefaultEmployeeDetailID))
          .map(a => ({ ...a, type: 'new' }));
        setNewAppraisals(filtered);
      })
      .catch(() => setNewAppraisals([]));
  }, []);

  // Combine once both fetches are done
  useEffect(() => {
    if (newAppraisals === null || appraisalLoading) return;

    try {
      const normalizedOldAppraisals = allOldAppraisals.map(normalizeOldAppraisal);
      const combined = [...newAppraisals, ...normalizedOldAppraisals]
        .sort((a, b) => new Date(b.EmployeeDueDate) - new Date(a.EmployeeDueDate));

      setAppraisals(combined);

      if (combined.length > 0) {
        const mostRecentYear = new Date(combined[0].EmployeeDueDate).getFullYear();
        setYear(mostRecentYear);
      }

      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [newAppraisals, appraisalLoading, allOldAppraisals]);

  const handleClick = (appraisal) => {
    if (appraisal.type === 'old') {
      context.router.push(`/appraisals/${appraisal.PMAppraisalID}`);
    } else {
      context.router.push(`/pmappraisals/${appraisal.PMEmployeeAppraisalID}`);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Col xs={12} sm={10} smOffset={1} md={8} mdOffset={2}>
        <PanelMessage
          type="error"
          iconName="error"
          message={error.message || <FormattedMessage id="appraisal.error" />}
        />
      </Col>
    );
  }

  // Extract unique years
  const years = [...new Set(appraisals.map(a => new Date(a.EmployeeDueDate).getFullYear()))].sort((a, b) => a - b);

  // Filter by year
  const filteredAppraisals = year
    ? appraisals.filter(a => new Date(a.EmployeeDueDate).getFullYear() === year)
    : appraisals;

  const getStatusMessage = (appraisal) => {
    const empDue = new Date(appraisal.EmployeeDueDate);
    empDue.setHours(23, 59, 59);
    const now = Date.now();

    if (appraisal.EmployeeStatus === FINISHED || appraisal.EmployeeStatus === 'Finished') {
      return <span style={{ color: pallet.successColor }}><FormattedMessage id="appraisal.answer" /></span>;
    }

    if (empDue.getTime() < now) {
      return <span style={{ color: pallet.errorColor }}><FormattedMessage id="appraisal.expired" /></span>;
    }

    if (appraisal.EmployeeStatus === STARTED || appraisal.EmployeeStatus === 'Started') {
      return <span style={{ color: pallet.primary1Color }}><FormattedMessage id="appraisal.inProgress" /></span>;
    }

    return <span style={{ color: pallet.warningColor }}><FormattedMessage id="appraisal.notStarted" /></span>;
  };

  const getStatusIcon = (status) => {
    if (status === FINISHED || status === 'Finished') return FINISHED;
    if (status === STARTED || status === 'Started') return STARTED;
    return NONE;
  };

  // Use custom appraisal title from WebValues if exists
  const appraisalTitle = getAppraisalTitle(valuesByKey);

  return (
    <Col xs={12} sm={12} md={10} mdOffset={1} style={{ paddingBottom: 16 }}>
      <Subheader style={styles.header}>
        <FormattedMessage id={appraisalTitle} />
      </Subheader>

      <Row middle="xs">
        <Col xs style={{ textAlign: 'left' }}>
          <SelectField
            floatingLabelText={<FormattedMessage id="appraisal.select.year" />}
            value={year}
            onChange={(e, index, value) => setYear(value)}
          >
            <MenuItem value={0} primaryText=" " />
            {years.map(y => (
              <MenuItem key={y} value={y} primaryText={y} />
            ))}
          </SelectField>

          {filteredAppraisals.length === 0 ? (
            <PanelMessage
              type="info"
              iconName="info"
              message={<FormattedMessage id="appraisal.noAppraisal" values={{ year }} />}
            />
          ) : (
            filteredAppraisals.map((appraisal) => {
              const empDue = new Date(appraisal.EmployeeDueDate);
              empDue.setHours(23, 59, 59);

              return (
                <Paper
                  key={appraisal.PMEmployeeAppraisalID}
                  style={styles.appraisal}
                  onClick={() => handleClick(appraisal)}
                  zDepth={1}
                >
                  <div style={styles.title}>
                    <StatusIcon type={getStatusIcon(appraisal.EmployeeStatus)} />
                    {get(appraisal.Title, appraisal.DefaultLanguage) || get(appraisal.Title, 'IS') || 'Appraisal'}
                  </div>

                  <div style={styles.date}>
                    <FormattedMessage id="expires" /> {convert(empDue)}
                  </div>

                  <Row>
                    <Col xs={12} sm={5} md={4}>
                      <div style={styles.info}>
                        <span style={styles.label}>
                          <FormattedMessage id="appraisal.employee" />
                        </span>
                        <span style={styles.value}>
                          <StatusIcon type={getStatusIcon(appraisal.EmployeeStatus)} />
                          {appraisal.EmployeeName}
                        </span>
                      </div>
                    </Col>

                    <Col xs={12} sm={5} md={4}>
                      <div style={styles.info}>
                        <span style={styles.label}>
                          <FormattedMessage id="appraisal.manager" />
                        </span>
                        <span style={styles.value}>
                          <StatusIcon type={getStatusIcon(appraisal.ManagerStatus)} />
                          {appraisal.ManagerEmployeeName}
                        </span>
                      </div>
                    </Col>

                    <Col xs={12} sm={2} md={4}>
                      <div style={{ ...styles.info, alignItems: 'flex-end' }}>
                        <span style={styles.statusMessage}>
                          {getStatusMessage(appraisal)}
                        </span>
                      </div>
                    </Col>
                  </Row>
                </Paper>
              );
            })
          )}
        </Col>
      </Row>
    </Col>
  );
};

PMAppraisals.propTypes = {
  valuesByKey: PropTypes.object,
};

PMAppraisals.contextTypes = {
  router: PropTypes.object.isRequired,
};

export default connect(state => ({ valuesByKey: state.WebValuesReducer.valuesByKey }))(PMAppraisals);



// WEBPACK FOOTER //
// ./src/containers/pmappraisals/PMAppraisals.js